import { Component, Input, Output, EventEmitter, signal, inject, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Feedback } from '../../../core/services/satisfaction.service';
import { MessageTemplate, MessageTemplateService } from '../../../core/services/message-template.service';
import { SessionService } from '../../../core/session.service';
import { ConfirmModalComponent } from '../../../component/confirm-modal/confirm-modal.component';
import { TimezoneService } from '../../../core/services/timezone.service';
import { formatInTimeZone } from 'date-fns-tz';

@Component({
  selector: 'app-reply-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmModalComponent, TranslatePipe],
  templateUrl: './reply-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reply-modal.component.scss']
})
export class ReplyModalComponent implements OnInit, OnChanges {
  private templateService = inject(MessageTemplateService);
  private session = inject(SessionService);
  private timezone = inject(TimezoneService);

  @Input() show = false;
  @Input() feedback: Feedback | null = null;
  
  @Output() close = new EventEmitter<void>();
  @Output() sendReply = new EventEmitter<{subject: string, message: string}>();
  @Output() noReply = new EventEmitter<void>();

  emailTemplates = signal<MessageTemplate[]>([]);
  selectedTemplate = '';
  replySubject = '';
  replyMessage = '';
  replyLoading = false;
  replySuccess = '';
  replyError = '';
  showConfirmNoReply = false;
  showConfirmSend = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['feedback'] && this.feedback && this.show) {
      this.initializeForm();
    }
    if (changes['show']) {
      if (this.show) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }

  ngOnInit() {
    if (this.feedback && this.show) {
      this.initializeForm();
    }
  }

  private initializeForm() {
    if (!this.feedback) return;
    
    this.replySubject = this.feedback.queue_name ? this.feedback.queue_name : '';
    this.replyMessage = '';
    this.selectedTemplate = '';
    this.loadEmailTemplates();
  }

  private getDefaultMessage(feedback: Feedback): string {
    return '';
  }

  private loadEmailTemplates() {
    const selectedId = this.session.selectedId();
    const establishment = this.session.establishments().find(e => e.id === selectedId);
    if (!establishment?.org_id) return;

    this.templateService.getTemplatesByType('EMAIL_REPLY', establishment.org_id).subscribe({
      next: (templates) => {
        this.emailTemplates.set(templates);
        // Sélectionner automatiquement le premier template
        if (templates.length > 0) {
          this.selectedTemplate = templates[0].name;
          this.onTemplateSelect();
        }
      },
      error: (err) => {
        console.error('Template loading failed:', err?.message?.replace(/[\r\n]/g, ' ') || 'Unknown error');
      }
    });
  }

  onTemplateSelect() {
    const template = this.emailTemplates().find(t => t.name === this.selectedTemplate);
    if (template) {
      this.replyMessage = template.content;
      if (template.subject) {
        this.replySubject = template.subject;
      }
    }
  }

  onSendReply() {
    if (!this.replyMessage.trim()) return;
    this.showConfirmSend = true;
  }

  onConfirmSend() {
    this.showConfirmSend = false;
    this.sendReply.emit({
      subject: this.replySubject,
      message: this.replyMessage
    });
    this.onClose();
  }

  onCancelSend() {
    this.showConfirmSend = false;
  }

  onClose() {
    this.replySubject = '';
    this.replyMessage = '';
    this.replyLoading = false;
    this.replySuccess = '';
    this.replyError = '';
    this.selectedTemplate = '';
    document.body.style.overflow = '';
    this.close.emit();
  }

  onNoReply() {
    this.showConfirmNoReply = true;
  }

  onConfirmNoReply() {
    this.showConfirmNoReply = false;
    this.noReply.emit();
    this.onClose();
  }

  onCancelNoReply() {
    this.showConfirmNoReply = false;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const formatted = formatInTimeZone(date, this.timezone.getEstablishmentTimezone(), 'dd/MM/yyyy HH:mm');
    return `${formatted}${this.timezone.getTimezoneHint(date)}`;
  }
}